import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Tracer (Act III, ACT3-BIBLE.md §8) — an Ostinato echo, and the act's rule at
 * creature scale.
 *
 * It does not walk toward you. It walks toward where you *were* a few seconds
 * ago, reading the same movement ring buffer the Ostinato's signature reads
 * (Player.pathHistory). If you keep moving forward it is permanently behind
 * you and completely harmless; the moment you double back along your own path
 * — the reflex a hundred runs have drilled — you walk into it.
 *
 * That's exactly the boss's lesson, taught for free during a wave stage: this
 * act punishes the *shape* of your movement, not its speed. Fragile and quick,
 * because the threat is where it is, not how much HP it has.
 */
export class Tracer {
  constructor(eventBus, { x, y, radius = 4, hp, moveSpeed, contactDamage, followDelay = 2.5 }) {
    this.eventBus = eventBus;
    this.type = 'tracer';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.followDelay = followDelay;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    // The player's own past, or — before the buffer has filled, and in any
    // headless caller that never ticks Player — the player themselves, so a
    // Tracer is never inert.
    const goal = player.positionAgo?.(this.followDelay) ?? player;
    const dx = goal.x - this.x;
    const dy = goal.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;
    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    const playerDist = Math.hypot(player.x - this.x, player.y - this.y);
    if (this._contactCooldown <= 0 && playerDist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
